import { useMemo, useState, type FormEvent } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import { useLacTenantsQuery } from '../../features/lac/queries/useLacTenantsQuery';
import { useI18n } from '../../i18n/useI18n';
import type { LacCreateTenantInput } from '../../types/lac';
import { containsWhitespace } from '../../utils/formUtils';
import './CreateLacTenantDialog.css';

type CreateLacTenantDialogProps = {
  onCreate: (input: LacCreateTenantInput) => void;
  onClose: () => void;
  isSubmitting?: boolean;
};

export function CreateLacTenantDialog({
  onCreate,
  onClose,
  isSubmitting = false,
}: CreateLacTenantDialogProps) {
  const { translate } = useI18n();

  const tenantsQuery = useLacTenantsQuery();
  const tenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);

  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [maxUsers, setMaxUsers] = useState('');
  const [maxChatbots, setMaxChatbots] = useState('');

  const trimmedName = name.trim();
  const isNameInvalid = trimmedName !== '' && containsWhitespace(name);
  const isDuplicateName =
    trimmedName !== '' && tenants.some((tenant) => tenant.name === trimmedName);

  const trimmedMaxUsers = maxUsers.trim();
  const parsedMaxUsers =
    trimmedMaxUsers === '' ? undefined : Number(trimmedMaxUsers);
  const isMaxUsersInvalid =
    trimmedMaxUsers !== '' &&
    (!Number.isInteger(parsedMaxUsers) ||
      parsedMaxUsers == null ||
      parsedMaxUsers < 1);

  const trimmedMaxChatbots = maxChatbots.trim();
  const parsedMaxChatbots =
    trimmedMaxChatbots === '' ? undefined : Number(trimmedMaxChatbots);
  const isMaxChatbotsInvalid =
    trimmedMaxChatbots !== '' &&
    (!Number.isInteger(parsedMaxChatbots) ||
      parsedMaxChatbots == null ||
      parsedMaxChatbots < 1);

  const isCreateDisabled =
    isSubmitting ||
    trimmedName === '' ||
    isNameInvalid ||
    isDuplicateName ||
    isMaxUsersInvalid ||
    isMaxChatbotsInvalid ||
    tenantsQuery.isLoading ||
    tenantsQuery.isError;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isCreateDisabled) {
      return;
    }

    onCreate({
      name: trimmedName,
      displayName: displayName.trim() || undefined,
      description: description.trim() || undefined,
      maxUsers: parsedMaxUsers,
      maxChatbots: parsedMaxChatbots,
    });
  };

  return (
    <LiksDialog
      isOpen
      as="form"
      size="md"
      title={translate('lac.organisations.create.title')}
      onClose={onClose}
      onSubmit={handleSubmit}
      closeLabel={translate('lac.organisations.create.close')}
      closeDisabled={isSubmitting}
      isDismissDisabled={isSubmitting}
      footer={
        <>
          <LiksButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {translate('lac.organisations.create.cancel')}
          </LiksButton>
          <LiksButton type="submit" disabled={isCreateDisabled}>
            {translate('lac.organisations.create.submit')}
          </LiksButton>
        </>
      }
    >
      {tenantsQuery.isError && (
        <p className="lac-create-tenant__hint" role="alert">
          {translate('lac.organisations.create.loadError')}
        </p>
      )}

      <div className="lac-create-tenant__row">
        <LiksTextFormField
          label={translate('lac.organisations.create.nameLabel')}
          required
          value={name}
          onChange={setName}
          autoFocus
          disabled={isSubmitting || tenantsQuery.isLoading}
          autoComplete="off"
          error={
            isNameInvalid
              ? translate('lac.organisations.create.nameNoSpaces')
              : isDuplicateName
                ? translate('lac.organisations.create.nameDuplicate')
                : null
          }
        />

        <LiksTextFormField
          label={translate('lac.organisations.create.displayNameLabel')}
          value={displayName}
          onChange={setDisplayName}
          disabled={isSubmitting}
          autoComplete="off"
          placeholder={trimmedName || undefined}
        />
      </div>

      <LiksTextFormField
        label={translate('lac.organisations.create.descriptionLabel')}
        multiline
        value={description}
        onChange={setDescription}
        disabled={isSubmitting}
        autoComplete="off"
      />

      <div className="lac-create-tenant__row">
        <LiksTextFormField
          label={translate('lac.organisations.create.maxUsersLabel')}
          type="number"
          value={maxUsers}
          onChange={setMaxUsers}
          disabled={isSubmitting}
          autoComplete="off"
          min={1}
          step={1}
          error={
            isMaxUsersInvalid
              ? translate('lac.organisations.create.maxUsersInvalid')
              : null
          }
        />

        <LiksTextFormField
          label={translate('lac.organisations.create.maxChatbotsLabel')}
          type="number"
          value={maxChatbots}
          onChange={setMaxChatbots}
          disabled={isSubmitting}
          autoComplete="off"
          min={1}
          step={1}
          error={
            isMaxChatbotsInvalid
              ? translate('lac.organisations.create.maxChatbotsInvalid')
              : null
          }
        />
      </div>
    </LiksDialog>
  );
}
